import React, { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { format, parseISO } from "date-fns";
import { dash } from "./dashboardTokens";

const CHART_HEIGHT = 100;

/**
 * Message volume — subtle grid, refined bars, total chip.
 */
export default function MessageVolumeBars({ series = [], total, bucket = "day" }) {
  const max = useMemo(() => Math.max(1, ...series.map((s) => s.count)), [series]);

  const formatTick = (point) => {
    if (point.label) return point.label;
    try {
      return format(parseISO(`${point.date}T12:00:00`), bucket === "month" ? "MMM yy" : "d MMM");
    } catch {
      return point.date?.slice(5) ?? "";
    }
  };

  if (!series.length) {
    return (
      <Box
        sx={{
          py: 4,
          px: 2,
          textAlign: "center",
          borderRadius: 2,
          bgcolor: alpha("#64748b", 0.06),
          border: `1px dashed ${dash.border}`,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No messages in this range.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Try a different date range or send traffic to this number.
        </Typography>
      </Box>
    );
  }

  const dense = series.length > 14;

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          overflowX: dense ? "auto" : "visible",
          pb: 1,
          mx: -0.5,
          px: 0.5,
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha("#64748b", 0.35),
            borderRadius: 3,
          },
        }}
      >
        {/* faint horizontal grid lines */}
        <Box
          sx={{
            position: "absolute",
            left: 8,
            right: 8,
            top: 24,
            height: CHART_HEIGHT,
            backgroundImage: `linear-gradient(to bottom, ${alpha("#94a3b8", 0.15)} 1px, transparent 1px)`,
            backgroundSize: "100% 25%",
            pointerEvents: "none",
            borderRadius: 1,
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: dense ? "flex-start" : "space-between",
            gap: dense ? 0.35 : 1,
            minHeight: CHART_HEIGHT + 52,
            minWidth: dense ? Math.min(800, series.length * 20) : "auto",
            position: "relative",
            pt: 3,
          }}
        >
          {series.map((point) => {
            const barPx = max ? (point.count / max) * CHART_HEIGHT : 0;
            return (
              <Box
                key={`${point.date}-${point.label || ""}`}
                sx={{
                  flex: dense ? "0 0 16px" : 1,
                  minWidth: dense ? 14 : 0,
                  maxWidth: dense ? 20 : 56,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: point.count > 0 ? "primary.main" : "text.disabled",
                    fontSize: 10,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {point.count}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    height: CHART_HEIGHT,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 36,
                      height: Math.max(barPx, point.count > 0 ? 6 : 3),
                      borderRadius: "6px 6px 2px 2px",
                      background:
                        point.count > 0
                          ? `linear-gradient(180deg, ${alpha("#2563eb", 0.95)} 0%, ${alpha("#1d4ed8", 0.85)} 100%)`
                          : alpha("#94a3b8", 0.2),
                      transition: "height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: dense ? 9 : 10,
                    textAlign: "center",
                    lineHeight: 1.2,
                    maxWidth: 56,
                    wordBreak: "break-word",
                  }}
                >
                  {formatTick(point)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      {total != null && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
            Total volume in range
          </Typography>
          <Chip
            label={`${total.toLocaleString()} messages`}
            size="small"
            sx={{
              fontWeight: 600,
              fontFeatureSettings: '"tnum"',
              bgcolor: alpha("#2563eb", 0.1),
              color: "primary.dark",
              border: "none",
            }}
          />
        </Box>
      )}
    </Box>
  );
}
